// client/src/components/LabelDesigner.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Card, Form, Button, Modal, Table } from 'react-bootstrap';
import axios from 'axios';
import JsBarcode from 'jsbarcode';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function LabelDesigner({ addAlert }) {
  const [designs, setDesigns] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentDesign, setCurrentDesign] = useState({
    design_name: '',
    brand_name: '',
    dress_type: 'Casual Wear',
    sku_code: '',
    price: '',
    size: 'M',
    color: 'Black',
    care_instructions: '',
    header_color: '#8a2be2'
  });
  const barcodeRef = useRef(null);
  const printAreaRef = useRef(null);

  useEffect(() => {
    fetchDesigns();
  }, []);

  useEffect(() => {
    if (barcodeRef.current && currentDesign.sku_code) {
      JsBarcode(barcodeRef.current, currentDesign.sku_code || 'TEST-123', {
        format: "CODE128",
        width: 1.5,
        height: 50,
        displayValue: true,
        fontSize: 12,
        background: "transparent"
      });
    }
  }, [currentDesign.sku_code]);

  const fetchDesigns = async () => {
    try {
      const response = await axios.get(`${API_URL}/designs/read.php`);
      setDesigns(response.data.records || []);
    } catch (error) {
      console.error('Error fetching designs:', error);
      addAlert('Failed to load designs', 'danger');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentDesign(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(`${API_URL}/designs/create.php`, currentDesign);
      addAlert('Design created successfully!', 'success');
      setShowModal(false);
      resetForm();
      fetchDesigns();
    } catch (error) {
      console.error('Error creating design:', error);
      addAlert('Failed to create design', 'danger');
    }
  };

  const resetForm = () => {
    setCurrentDesign({
      design_name: '',
      brand_name: '',
      dress_type: 'Casual Wear',
      sku_code: '',
      price: '',
      size: 'M',
      color: 'Black',
      care_instructions: '',
      header_color: '#8a2be2'
    });
  };

  const applyTemplate = (template) => {
    const templates = {
      premium: {
        design_name: 'Premium Collection',
        brand_name: 'LUXURY BRAND',
        dress_type: 'Formal Wear',
        sku_code: 'LUX-2023-L-BK',
        price: '199.99',
        size: 'L',
        color: 'Black',
        care_instructions: 'Dry Clean Only • Do Not Bleach • Hang Dry',
        header_color: '#2c3e50'
      },
      casual: {
        design_name: 'Summer Casual',
        brand_name: 'SUNSET WEAR',
        dress_type: 'Casual Wear',
        sku_code: 'SS-2023-M-NV',
        price: '39.99',
        size: 'M',
        color: 'Navy Blue',
        care_instructions: '100% Cotton • Machine Wash Cold • Tumble Dry Low',
        header_color: '#3498db'
      },
      kids: {
        design_name: 'Kids Fun Wear',
        brand_name: 'KIDZ ZONE',
        dress_type: 'Kids Wear',
        sku_code: 'KZ-2023-S-RD',
        price: '24.99',
        size: 'S',
        color: 'Red',
        care_instructions: '100% Cotton • Machine Wash Warm • Do Not Iron',
        header_color: '#e74c3c'
      }
    };
    
    setCurrentDesign(templates[template]);
  };

  const printDesign = () => {
    if (!printAreaRef.current) {
      addAlert('Print preview is not ready yet.', 'warning');
      return;
    }

    const printWindow = window.open('', '_blank', 'width=600,height=800');
    if (!printWindow) {
      addAlert('Unable to open print window. Please allow popups.', 'warning');
      return;
    }

    const printStyles = `
      <style>
        @page { margin: 0; }
        body { margin: 0; padding: 12px; font-family: Arial, sans-serif; }
        .sticker-preview {
          border: 2px solid #8a2be2;
          border-radius: 8px;
          width: 200px;
          height: 320px;
          background: white;
          overflow: hidden;
        }
        .sticker-header {
          background: linear-gradient(90deg, #8a2be2, #9b59b6);
          color: white;
          padding: 8px;
          text-align: center;
          font-weight: bold;
        }
        .sticker-body { padding: 15px; text-align: center; }
        .brand-name { font-size: 1.2rem; font-weight: bold; color: #2c3e50; margin-bottom: 10px; }
        .price-tag { color: #e74c3c; font-weight: bold; font-size: 1.3rem; }
        .text-muted { color: #6c757d; }
        .fw-bold { font-weight: bold; }
        .small { font-size: 0.875rem; }
        .barcode-container { margin: 8px 0; }
        .d-flex { display: flex; }
        .justify-content-between { justify-content: space-between; }
        .mb-3 { margin-bottom: 1rem; }
        .mt-3 { margin-top: 1rem; }
      </style>
    `;

    printWindow.document.write(`
      <html>
        <head>
          <title>Print Label</title>
          ${printStyles}
        </head>
        <body>
          ${printAreaRef.current.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Label Designer</h2>
        <Button variant="purple" onClick={() => setShowModal(true)}>
          <i className="fas fa-plus me-2"></i>
          New Design
        </Button>
      </div>

      <Row>
        {/* Design Preview */}
        <Col lg={6} className="mb-4">
          <Card>
            <Card.Body>
              <Card.Title>Design Preview</Card.Title>
              <div className="d-flex justify-content-center">
                <div className="sticker-preview" ref={printAreaRef}>
                  <div 
                    className="sticker-header"
                    style={{ background: `linear-gradient(90deg, ${currentDesign.header_color}, ${lightenColor(currentDesign.header_color, 20)})` }}
                  >
                    {currentDesign.dress_type?.toUpperCase() || 'DRESS LABEL'}
                  </div>
                  <div className="sticker-body text-center">
                    <div className="brand-name">{currentDesign.brand_name || 'BRAND NAME'}</div>
                    <div className="price-tag mb-3">
                      ${currentDesign.price || '0.00'}
                    </div>
                    <div className="d-flex justify-content-between mb-3">
                      <div>
                        <div className="text-muted small">Size</div>
                        <div className="fw-bold">{currentDesign.size || 'M'}</div>
                      </div>
                      <div>
                        <div className="text-muted small">Color</div>
                        <div className="fw-bold">{currentDesign.color || 'Black'}</div>
                      </div>
                    </div>
                    <div className="small text-muted mb-3">
                      SKU: {currentDesign.sku_code || 'SKU-000'}
                    </div>
                    <div className="barcode-container">
                      <svg ref={barcodeRef}></svg>
                    </div>
                    <div className="small text-muted mt-3">
                      {currentDesign.care_instructions || 'Care instructions here'}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 text-center">
                <Button variant="purple" className="me-2" onClick={printDesign}>
                  <i className="fas fa-print me-2"></i>
                  Print Test
                </Button>
                <Button variant="outline-primary" onClick={() => applyTemplate('premium')}>
                  Premium Template
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Design List */}
        <Col lg={6}>
          <Card>
            <Card.Body>
              <Card.Title>Existing Designs</Card.Title>
              {designs.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="fas fa-palette fa-3x mb-3"></i>
                  <p>No designs yet. Create your first design!</p>
                </div>
              ) : (
                <Table hover>
                  <thead>
                    <tr>
                      <th>Design Name</th>
                      <th>SKU</th>
                      <th>Type</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {designs.map((design) => (
                      <tr key={design.id}>
                        <td>{design.design_name}</td>
                        <td>
                          <code>{design.sku_code}</code>
                        </td>
                        <td>
                          <span className="badge bg-secondary">{design.dress_type}</span>
                        </td>
                        <td>
                          <Button size="sm" variant="outline-primary" className="me-1">
                            <i className="fas fa-edit"></i>
                          </Button>
                          <Button size="sm" variant="outline-danger">
                            <i className="fas fa-trash"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Create Design Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New Label Design</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Design Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="design_name"
                    value={currentDesign.design_name}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Brand Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="brand_name"
                    value={currentDesign.brand_name}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Dress Type *</Form.Label>
                  <Form.Select
                    name="dress_type"
                    value={currentDesign.dress_type}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="Casual Wear">Casual Wear</option>
                    <option value="Formal Wear">Formal Wear</option>
                    <option value="Sports Wear">Sports Wear</option>
                    <option value="Kids Wear">Kids Wear</option>
                    <option value="Winter Wear">Winter Wear</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>SKU Code *</Form.Label>
                  <Form.Control
                    type="text"
                    name="sku_code"
                    value={currentDesign.sku_code}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Price ($) *</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="price"
                    value={currentDesign.price}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Size *</Form.Label>
                  <Form.Select
                    name="size"
                    value={currentDesign.size}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="XS">XS</option>
                    <option value="S">S</option>
                    <option value="M">M</option>
                    <option value="L">L</option>
                    <option value="XL">XL</option>
                    <option value="XXL">XXL</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Color *</Form.Label>
                  <Form.Select
                    name="color"
                    value={currentDesign.color}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="Black">Black</option>
                    <option value="White">White</option>
                    <option value="Navy Blue">Navy Blue</option>
                    <option value="Red">Red</option>
                    <option value="Green">Green</option>
                    <option value="Gray">Gray</option>
                    <option value="Beige">Beige</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Header Color</Form.Label>
                  <Form.Select
                    name="header_color"
                    value={currentDesign.header_color}
                    onChange={handleInputChange}
                  >
                    <option value="#8a2be2">Purple</option>
                    <option value="#2c3e50">Dark Blue</option>
                    <option value="#c0392b">Red</option>
                    <option value="#16a085">Teal</option>
                    <option value="#f39c12">Orange</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Care Instructions</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="care_instructions"
                value={currentDesign.care_instructions}
                onChange={handleInputChange}
                placeholder="Example: 100% Cotton • Machine Wash Cold • Tumble Dry Low"
              />
            </Form.Group>

            <div className="d-flex gap-2">
              <Button variant="outline-success" onClick={() => applyTemplate('premium')}>
                Premium Template
              </Button>
              <Button variant="outline-info" onClick={() => applyTemplate('casual')}>
                Casual Template
              </Button>
              <Button variant="outline-warning" onClick={() => applyTemplate('kids')}>
                Kids Template
              </Button>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="purple" type="submit">
              <i className="fas fa-save me-2"></i>
              Save Design
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

// Helper function to lighten color
function lightenColor(color, percent) {
  const num = parseInt(color.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  
  return "#" + (
    0x1000000 +
    (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
    (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
    (B < 255 ? B < 1 ? 0 : B : 255)
  ).toString(16).slice(1);
}

export default LabelDesigner;